import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import {
  Gauge,
  Package,
  Flask,
  Factory,
  Barcode,
  ListNumbers,
  Printer,
  SignOut,
  Warehouse,
  ShoppingCart,
  ArrowsClockwise,
  CurrencyEur,
  Drop,
  Key,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";

const nav = [
  { to: "/", label: "Dashboard", icon: Gauge, end: true, tid: "nav-dashboard" },
  { to: "/ordini", label: "Ordini", icon: ShoppingCart, tid: "nav-ordini" },
  { to: "/prodotti", label: "Prodotti", icon: Package, tid: "nav-prodotti" },
  { to: "/listino", label: "Listino Acquisti", icon: CurrencyEur, tid: "nav-listino" },
  { to: "/formule", label: "Formule & Distinte", icon: Flask, tid: "nav-formule" },
  { to: "/plastiche", label: "Lavorazioni Plastiche", icon: Drop, tid: "nav-plastiche" },
  { to: "/produzione", label: "Produzione", icon: Factory, tid: "nav-produzione" },
  { to: "/conversioni", label: "Conversioni", icon: ArrowsClockwise, tid: "nav-conversioni" },
  { to: "/scanner", label: "Scanner", icon: Barcode, tid: "nav-scanner" },
  { to: "/movimenti", label: "Movimenti", icon: ListNumbers, tid: "nav-movimenti" },
  { to: "/etichette", label: "Etichette", icon: Printer, tid: "nav-etichette" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password aggiornata. Effettua di nuovo il login.");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      // Force re-login since session is still valid but we want to verify the new one
      await logout();
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      <aside
        className="w-64 bg-[#0A0A0A] text-white flex flex-col border-r border-[#1f1f1f]"
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-[#1f1f1f] flex items-center gap-3">
          <div className="w-9 h-9 bg-white text-[#0A0A0A] flex items-center justify-center">
            <Warehouse size={22} weight="bold" />
          </div>
          <div>
            <div className="font-display font-semibold text-sm tracking-tight">
              MAGAZZINO
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-gray-500">
              Gestione Industriale
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-display font-medium transition-colors ${
                  isActive
                    ? "bg-white text-[#0A0A0A]"
                    : "text-gray-300 hover:bg-[#1a1a1a]"
                }`
              }
            >
              <n.icon size={18} weight="bold" />
              <span className="uppercase tracking-wider text-xs">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#1f1f1f]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1">
            Operatore
          </div>
          <div className="text-sm font-display truncate" data-testid="current-user-name">
            {user?.name}
          </div>
          <div className="text-xs text-gray-400 truncate mb-3" data-testid="current-user-email">
            {user?.email}
          </div>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="change-password-btn"
            className="w-full flex items-center justify-center gap-2 bg-transparent border border-[#333] text-gray-300 hover:bg-[#1a1a1a] px-3 py-2 text-xs font-medium uppercase tracking-wider transition-colors mb-2"
          >
            <Key size={14} weight="bold" />
            Cambia Password
          </button>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 bg-transparent border border-[#333] text-gray-300 hover:bg-[#1a1a1a] px-3 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
          >
            <SignOut size={14} weight="bold" />
            Esci
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto bg-[#F9FAFB]" data-testid="main-content">
        <Outlet />
      </main>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Key size={18} weight="bold" /> Cambia Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPw} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Password attuale
              </label>
              <input
                type="password"
                required
                autoFocus
                data-testid="current-password-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Nuova password (min. 6 caratteri)
              </label>
              <input
                type="password"
                required
                minLength={6}
                data-testid="new-password-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Conferma nuova password
              </label>
              <input
                type="password"
                required
                data-testid="confirm-password-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setPwOpen(false)}
                className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={pwBusy}
                data-testid="submit-change-password"
                className="bg-[#1D3557] hover:bg-[#2A4B7C] disabled:opacity-50 text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider"
              >
                {pwBusy ? "Salvataggio…" : "Cambia password"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
